import {ArrowRight, Calendar, User} from 'lucide-react';
import {useNavigate} from "react-router-dom";
import * as React from "react";
import { graphql, useLazyLoadQuery } from 'react-relay';
import { BlogListQuery} from "./__generated__/BlogListQuery.graphql";
import {BRAND} from "../brand";

const QUERY = graphql`
  query BlogListQuery {
    blogs {
        nodes {
            id
            title
            excerpt
            tags
            date
            author {
                name
            }
            image
        }
    }
  }
`;


interface Props {
    limit?: number;
}

const Blog: React.FC<Props> = ({limit}) => {
    const navigate = useNavigate();
    const data = useLazyLoadQuery<BlogListQuery>(QUERY, {});
    const posts = limit ? data.blogs.nodes.slice(0, limit) : data.blogs.nodes;
    const navigateBlogPost = (id: string) => {
        return () => navigate(`/blogs/${id}`);
    };
    return (
        <section id="insights" className="py-24 bg-lumina-dark relative border-t border-gray-900">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="mb-16">
                    <span
                        className="text-lumina-accent font-display text-sm tracking-wider uppercase">Knowledge Base</span>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-white mt-2">
                        {BRAND.wordmark} <span className="text-gray-600">INSIGHTS</span>
                    </h2>
                    <p className="text-gray-400 mt-4 max-w-2xl">
                        Technical deep dives into our hardware stack. Learn how we utilize advanced scanning and
                        fabrication technology.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map((post) => (
                        <div
                            key={post.id}
                            onClick={navigateBlogPost(post.id)}
                            className="group bg-[#0a0a0f] border border-gray-800 rounded-xl overflow-hidden cursor-pointer hover:border-lumina-accent/50 hover:-translate-y-2 transition-all duration-300"
                        >
                            <div className="h-48 overflow-hidden relative">
                                <div
                                    className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10"></div>
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute top-4 left-4 z-20 flex gap-2">
                                    {post.tags.slice(0, 1).map(tag => (
                                        <span key={tag}
                                              className="px-2 py-1 bg-black/70 backdrop-blur-md text-lumina-accent text-[10px] font-bold uppercase tracking-wider border border-lumina-accent/20 rounded">
                      {tag}
                    </span>
                                    ))}
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                                    <span className="flex items-center gap-1"><Calendar
                                        className="w-3 h-3"/> {new Date(post.date).toLocaleDateString()}</span>
                                    <span className="flex items-center gap-1"><User className="w-3 h-3"/> {post.author.name}</span>
                                </div>

                                <h3 className="text-xl font-display font-bold text-white mb-3 group-hover:text-lumina-accent transition-colors leading-tight">
                                    {post.title}
                                </h3>

                                <p className="text-gray-400 text-sm line-clamp-3 mb-6">
                                    {post.excerpt}
                                </p>

                                <div
                                    className="flex items-center text-lumina-purple text-xs font-bold uppercase tracking-widest group-hover:text-white transition-colors">
                                    Read Article <ArrowRight
                                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"/>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Blog;
